import { DeleteOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import {
  HwTypesResponseDTO,
  IHwTypeBase,
  ItemGroupConfigurationRequest,
  Property,
  PropsResponseDTO,
  Subsystem,
  SubsystemsResponseDTO,
} from '@dto/architecture.dto';
import {
  ConfigurationChangeFilterDTO,
  GetConfigurationChangeFilterRule,
  SaveConfigurationChangeFilterRule,
} from '@dto/configurationChanges.dto';
import {
  Button,
  ButtonProps,
  Card,
  Form,
  FormInstance,
  FormListFieldData,
  Popconfirm,
  SelectProps,
  Spin
} from 'antd';
import { FormProps } from 'antd/es/form';
import { NamePath, StoreValue } from 'antd/es/form/interface';
import { AxiosError, AxiosResponse } from 'axios';
import { isEqual } from 'lodash-es';
import { useEffect, useState } from 'react';
import { XmButton } from 'src/component/button/XmButton';
import { XmInput } from 'src/component/input/XmInput';
import XmPreviewRuleForm from 'src/component/previewRuleForm/XmPreviewRuleForm';
import { XmSelect } from 'src/component/select/XmSelect';
import { CustomFormCompProps } from 'src/component/selectItems/comp/Items';
import XmRule, { defaultRule, formatRuleDataForApi } from 'src/component/selectItems/XmRule';
import { RuleGroup } from 'src/component/selectItems/XmRuleList';
import XmTabsSticky from 'src/component/stickyTab/XmTabsSticky';
import { isClassLPAR } from 'src/data/Class';
import useGetApiData from 'src/hook/useGetApiData';
import { Log } from 'src/service/log.service';
import { postApi, putApi } from 'src/util/apiCalls';
import { GLOB } from 'src/util/Glob';
import useCfgFilterStore from '../../CfgFilterStore';
import { FilterModalProps } from './Modal';

export interface FormFilterInstance {
  label: string;
  group_id: number;
  rules: IRule[];
}

type FilterFormProps = Omit<FilterModalProps, 'onOk'> & FormProps<FormFilterInstance>;

const FilterForm = ({
  form,
  onFormFinish,
  label,
  classOptions,
  folders,
  isLoadingClassAndFolders,
  usedFilterLabels,
  data,
  isCreate,
  filterId,
  groupId
}: FilterFormProps) => {
  const [activeTab, setActiveTab] = useState<string>('0');
  const [activeIndexTab, setActiveIndexTab] = useState<number>(0);
  const [isLoadingFormData, setIsLoadingFormData] = useState(false);
  const [isRuleEdited, setIsRuleEdited] = useState(false);
  const [lastKeyTab, setLastKeyTab] = useState<string>(null);
  const [isDisabledAddButton, setIsDisabledAddButton] = useState(false);

  const { readonly } = GLOB.userInfo;

  const setFormSaved = useCfgFilterStore(s => s.setFormSaved);

  const handleFinish = async (values: FormFilterInstance) => {
    const payload: SaveConfigurationChangeFilterRule = {
      ...values,
      rules: values.rules.map(formatRuleDataForApi),
    };

    try {
      setIsLoadingFormData(true);
      let response: AxiosResponse<ConfigurationChangeFilterDTO, SaveConfigurationChangeFilterRule>;

      if (isCreate) {
        response = await postApi<ConfigurationChangeFilterDTO, SaveConfigurationChangeFilterRule>(
          `/api/configuration/v1/tracker/filters`,
          payload
        );
      } else {
        response = await putApi<ConfigurationChangeFilterDTO, SaveConfigurationChangeFilterRule>(
          `/api/configuration/v1/tracker/filter/${filterId}`,
          payload
        );
      }

      onFormFinish(response.data.data);
      setFormSaved(true);
    } catch (error) {
      Log.error(
        `Failed to ${isCreate ? 'create' : 'update'} filter ${values.label}!`,
        error as AxiosError
      );
    } finally {
      setIsLoadingFormData(false);
      Log.success(`${isCreate ? 'Created' : 'Updated'} filter ${values.label}!`);
      setTimeout(() => setFormSaved(false), 100);
    }
  };

  const handleTabChange = (key: string, index: number) => {
    setActiveTab(key);
    setActiveIndexTab(index);
    setIsRuleEdited(false);
  };

  const handleAddRule = (add: (defaultValue?: StoreValue, insertIndex?: number) => void, currentRules: FormListFieldData[]) =>
    () => {
      setIsDisabledAddButton(true);
      const newIndex = currentRules.length;
      add({ ...defaultRuleTab }, newIndex);
      setTimeout(() => {
        const lastKey = lastKeyTab ? Number(lastKeyTab) + 1 : newIndex;
        setActiveTab(`${lastKey}`);
        setActiveIndexTab(currentRules.length);
        setLastKeyTab(state => state === null ? String(currentRules[currentRules.length - 1].key + 1) : String(+state + 1));
      }, 0);
      setTimeout(() => {
        setIsDisabledAddButton(false);
      }, 0);
    };

  const handleRemoveRule = (remove: (index: number) => void, currentRules: FormListFieldData[]) => (indexToRemove: number) => {
    const lastKey = currentRules[currentRules.length - 1].key.toString();
    setLastKeyTab(state => state > lastKey ? state : lastKey);
    const activeTabKey = activeTab;

    let newActiveKey = activeTabKey;

    if (indexToRemove === currentRules.length - 1) {
      const prevRule = currentRules[Math.max(0, indexToRemove - 1)];
      newActiveKey = prevRule.key.toString();
    } else if (currentRules[indexToRemove].key.toString() === activeTabKey) {
      const nextRule = currentRules[indexToRemove + 1] || currentRules[indexToRemove - 1];
      if (nextRule) {
        newActiveKey = nextRule.key.toString();
      }
    }

    remove(indexToRemove);

    setTimeout(() => {
      setActiveTab(newActiveKey);
      if (indexToRemove === 0) {
        setActiveIndexTab(0);
        return;
      }
      if (indexToRemove === currentRules.length - 1) {
        setActiveIndexTab(indexToRemove - 1);
        return;
      }
      if (indexToRemove === currentRules[indexToRemove].name) {
        setActiveIndexTab(indexToRemove);
        return;
      }
    }, 0);
  };


  const handleValuesChange = (changedValues: StoreValue) => {
    if (['items', 'regex', 'tags', 'exclude'].some(key => Object.keys(changedValues?.rules?.[activeTab] || {}).includes(key))) {
      setIsRuleEdited(true);
    }
  };

  return (
    <Spin spinning={isLoadingFormData}>
      <Form<FormFilterInstance>
        form={form}
        onFinish={handleFinish}
        initialValues={makeInitFormValues(label, groupId, data)}
        onValuesChange={handleValuesChange}
      >
        <Card className='xm-card-label-and-share'>
          <Form.Item
            className='xm-form-item-last'
            label='Filter label'
            name='label'
            rules={[
              { required: true, message: 'Please select label!' },
              {
                validator: (_, label: string) => {
                  if (usedFilterLabels?.some((l) => l === label))
                    return Promise.reject(new Error('This label is already used!'));

                  return Promise.resolve();
                },
              },
            ]}
          >
            <XmInput placeholder='Select label' maxLength={60} allowClear />
          </Form.Item>
          <Form.Item
            className='xm-form-item-last xm-form-item-share'
            label='Share group'
            name='group_id'
          >
            <XmSelect
              options={folders}
              loading={isLoadingClassAndFolders}
              placeholder='Select Group'
              allowClear
              fieldNames={{ value: 'group_id' }}
              ignoreReadonly
            />
          </Form.Item>
        </Card>
        <Card>
          <Form.List name='rules'>
            {(rules, { add, remove, }) => {
              const addHandler = handleAddRule(add, rules);
              const removeHandler = handleRemoveRule(remove, rules);

              return (
                <XmTabsSticky
                  className='xm-tab-filters rules'
                  activeKey={activeTab}
                  tabPlacement='start'
                  onTabClick={(key) => {
                    const index = rules.findIndex(r => r.key.toString() === key);
                    handleTabChange(key, index);
                  }}
                  tabBarExtraContent={{
                    left: (
                      <XmButton className='xm-btn-add-rule' onClick={addHandler} disabled={isDisabledAddButton}>
                        Add Rule
                      </XmButton>
                    ),
                  }}
                  items={rules.map(({ name, key }, index) => {
                    return {
                      key: key.toString(),
                      children: (
                        <RuleFormItems
                          form={form}
                          name={name}
                          classFormItem={{ loading: isLoadingClassAndFolders, options: classOptions }}
                          activeIndexTab={activeIndexTab}
                          isRuleEdited={isRuleEdited}
                        />
                      ),
                      label: (
                        <Form.Item
                          shouldUpdate={(prevValues, curValues) => {
                            const prevErrors = form.getFieldError(['rules', index]);
                            const curErrors = form.getFieldError(['rules', index]);
                            const prevNested = form
                              .getFieldsError()
                              .some(
                                (err) =>
                                  Array.isArray(err.name) &&
                                  err.name[0] === 'rules' &&
                                  err.name[1] === index &&
                                  err.errors.length > 0
                              );
                            const curNested = form
                              .getFieldsError()
                              .some(
                                (err) =>
                                  Array.isArray(err.name) &&
                                  err.name[0] === 'rules' &&
                                  err.name[1] === index &&
                                  err.errors.length > 0
                              );
                            const ruleChanged = prevValues.rules[index] !== curValues.rules[index];
                            return (
                              ruleChanged ||
                              prevErrors.length !== curErrors.length ||
                              prevNested !== curNested
                            );
                          }}
                          noStyle
                        >
                          {() => {
                            const inErrorState = form
                              .getFieldsError()
                              .some(
                                (err) =>
                                  Array.isArray(err.name) &&
                                  err.name[0] === 'rules' &&
                                  err.name[1] === index &&
                                  err.errors.length > 0
                              );

                            return (
                              <FilterTabLabel
                                label={`Rule #${index + 1}`}
                                onDelete={() => removeHandler(index)}
                                disabled={readonly || (rules.length < 2)}
                                inErrorState={inErrorState}
                              />
                            );
                          }}
                        </Form.Item>
                      ),
                    };
                  })}
                />
              );
            }}
          </Form.List>
        </Card>
      </Form>
    </Spin>
  );
};

function makeInitFormValues(
  label: string,
  group_id: number,
  data: GetConfigurationChangeFilterRule[]
): FormFilterInstance {
  if (!data?.length) return { label, group_id: undefined, rules: [{ ...defaultRuleTab }] };

  return {
    label,
    group_id,
    rules: data.map(
      ({ item_group_id: _, items, parents, ...rule }): IRule => ({
        ...rule,
        parents,
        items: items?.map(({ item_id }) => ({ item_id, disabled: false })),
      })
    ),
  };
}

interface PropsFilterTabLabel extends ButtonProps {
  label: string;
  inErrorState: boolean;
  onDelete: () => void;
}

const FilterTabLabel = ({ label, onDelete, inErrorState, ...rest }: PropsFilterTabLabel) => {
  return (
    <div className={inErrorState ? 'xm-filter-label error' : 'xm-filter-label'}>
      <span>{label}</span>
      <Popconfirm
        disabled={rest.disabled}
        title={
          <>
            Delete{' '}
            <span style={{ fontWeight: 'bold' }}>
              {label.length > 10
                ? `${label.slice(0, 3)}...${label.slice(label.length - 6, label.length)}`
                : label}
            </span>{' '}
            ?
          </>
        }
        icon={<QuestionCircleOutlined />}
        onConfirm={onDelete}
      >
        <Button
          {...rest}
          size='small'
          type='link'
          title='Delete filter'
          className={rest.disabled ? 'xm-btn first' : 'xm-btn first hover-danger'}
          icon={<DeleteOutlined />}
        />
      </Popconfirm>
    </div>
  );
};

interface IRule extends RuleGroup {
  filter_rule_id: string;
  class: string;
  hw_type: string;
  subsystem: string;
  properties: string[];
}

interface RuleFormItemsProps {
  classFormItem: SelectProps;
  name: number;
  form: FormInstance<FormFilterInstance>;
  activeIndexTab: number;
  isRuleEdited: boolean;
}

const RuleFormItems = ({ name, classFormItem, form, activeIndexTab, isRuleEdited }: RuleFormItemsProps) => {
  const { setFieldValue, getFieldValue } = form;
  const path = ['rules', name];
  const handleChange =
    ({
      hwType = true,
      subsystem = true,
      properties = true,
    }: Partial<Record<'hwType' | 'subsystem' | 'properties', boolean>>) =>
      () => {
        const reset: Partial<Record<'hw_type' | 'subsystem' | 'properties', unknown>> = {};
        if (hwType) reset.hw_type = undefined;

        if (subsystem) reset.subsystem = undefined;

        if (properties) reset.properties = [];

        setFieldValue([...path] as NamePath, {
          ...getFieldValue([...path] as NamePath),
          ...defaultRule,
          ...reset,
        });
      };

  const { readonly } = GLOB.userInfo;

  return (
    <>
      <Form.Item
        label='Device class'
        name={[name, 'class']}
        rules={[{ required: true, message: 'Please select Class!' }]}
      >
        <XmSelect
          {...classFormItem}
          disabled={readonly}
          placeholder='Select class'
          allowClear
          fieldNames={{ value: 'class' }}
          onChange={handleChange({})}
          ignoreReadonly
        />
      </Form.Item>
      <Form.Item
        noStyle
        shouldUpdate={(prev: FormFilterInstance, current) =>
          prev.rules[name]?.class !== current.rules[name]?.class
        }
      >
        {() => {
          const cls = getFieldValue([...path, 'class'] as NamePath);
          if (isClassLPAR(cls) || cls === undefined)
            return (
              <Form.Item
                label='HW type'
                name={[name, 'hw_type']}
                required
                rules={[{ required: true, message: 'Please select HW type!' }]}
              >
                <HwTypeSelect
                  cls={cls}
                  disabled={readonly || !cls}
                  onChange={handleChange({ hwType: false })}
                  apiParams={{ active: true, class: cls, type: 'tracker' }}
                />
              </Form.Item>
            );
        }}
      </Form.Item>
      <Form.Item
        noStyle
        shouldUpdate={(prev: FormFilterInstance, current) => {
          if (!isClassLPAR(prev.rules[name]?.class) && isClassLPAR(current.rules[name]?.class))
            return true;

          if (isClassLPAR(current.rules[name]?.class))
            return prev.rules[name]?.hw_type !== current.rules[name]?.hw_type;

          return prev.rules[name]?.class !== current.rules[name]?.class;
        }}
      >
        {() => (
          <Form.Item
            label='Subsystem'
            name={[name, 'subsystem']}
            rules={[{ required: true, message: 'Please select Subsystem!' }]}
          >
            <SubsystemSelect
              cls={getFieldValue([...path, 'class'] as NamePath)}
              hwType={getFieldValue([...path, 'hw_type'] as NamePath)}
              disabled={readonly
                || (isClassLPAR(getFieldValue([...path, 'class'] as NamePath))
                  ? !getFieldValue([...path, 'hw_type'] as NamePath)
                  : !getFieldValue([...path, 'class'] as NamePath))
              }
              onChange={handleChange({ hwType: false, subsystem: false })}
            />
          </Form.Item>
        )}
      </Form.Item>
      <Form.Item
        noStyle
        shouldUpdate={(prev: FormFilterInstance, current) =>
          !isEqual(prev?.rules?.[name], current?.rules?.[name])
        }
      >
        {() => (
          <XmRule
            cls={getFieldValue([...path, 'class'] as NamePath)}
            hw_type={getFieldValue([...path, 'hw_type'] as NamePath)}
            subsystem={getFieldValue([...path, 'subsystem'] as NamePath)}
            mountPoint={[name]}
            dependenciesPoint={[...path]}
          />
        )}
      </Form.Item>
      <XmPreviewRuleForm activeIndexTab={activeIndexTab} />
      <Form.Item
        noStyle
        shouldUpdate={(prev: FormFilterInstance, current) => {
          const rerenderKeys: (keyof IRule)[] = ['exclude', 'items', 'tags', 'parents', 'regex'];
          const hasChanged = rerenderKeys.some(
            (key) => prev.rules[name]?.[key] !== current.rules[name]?.[key]
          );
          return hasChanged;
        }}
      >
        {() => (
          <Form.Item
            className='xm-form-item-last'
            label='Properties'
            name={[name, 'properties']}
            rules={[{ required: true, message: 'Please select Properties!' }]}
          >
            <PropertiesSelect
              rule={getFieldValue([...path] as NamePath)}
              disabled={readonly || isPropertiesDisabled(getFieldValue([...path] as NamePath))}
              path={path}
              isRuleEdited={isRuleEdited}
            />
          </Form.Item>
        )}
      </Form.Item>
    </>
  );
};

function isPropertiesDisabled(rule: IRule): boolean {
  if (rule.items?.length || rule.tags?.length || rule.regex?.length) return false;

  return true;
}

const defaultRuleTab: IRule = {
  ...defaultRule,
  filter_rule_id: undefined,
  properties: [],
  class: undefined,
  hw_type: undefined,
  subsystem: undefined,
};

export type HwTypeSelectProps = CustomFormCompProps<string> &
  SelectProps & {
    cls: string;
    apiParams?: unknown;
  };

export const HwTypeSelect = ({ cls, apiParams, ...rest }: HwTypeSelectProps) => {
  const [hwTypeOptions, , isLoadingHwTypeOptions] = useGetApiData<
    IHwTypeBase[],
    HwTypesResponseDTO
  >(
    `/api/architecture/v1/hw_types`,
    `Failed to get hw type list.`,
    [cls],
    undefined,
    !!cls ? 'YES' : 'NO',
    true,
    apiParams
  );

  return (
    <XmSelect
      {...rest}
      options={hwTypeOptions}
      loading={isLoadingHwTypeOptions}
      placeholder='Select HW type'
      allowClear
      fieldNames={{ value: 'hw_type' }}
      ignoreReadonly
    />
  );
};

type SubsystemSelectProps = HwTypeSelectProps & {
  hwType: string;
};

const SubsystemSelect = ({ cls, hwType, apiParams, ...rest }: SubsystemSelectProps) => {
  const [subsystemOptions, , isLoadingSubsystemOptions] = useGetApiData<
    Subsystem[],
    SubsystemsResponseDTO
  >(
    isClassLPAR(cls)
      ? `/api/architecture/v1/hw_types/${hwType}/subsystems?type=tracker&active=true`
      : `/api/architecture/v1/subsystems?type=tracker&active=true`,
    `Failed to get subsystem list.`,
    [cls, hwType],
    undefined,
    isClassLPAR(cls) ? (!!cls && !!hwType ? 'YES' : 'NO') : !!cls ? 'YES' : 'NO',
    undefined,
    isClassLPAR(cls) ? {} : { class: cls }
  );

  return (
    <XmSelect
      {...rest}
      options={subsystemOptions}
      loading={isLoadingSubsystemOptions}
      placeholder='Select Subsystem'
      allowClear
      fieldNames={{ value: 'subsystem' }}
      ignoreReadonly
    />
  );
};

type PropertiesSelectProps = CustomFormCompProps<string> &
  SelectProps & {
    rule: IRule;
    path: (string | number)[];
    isRuleEdited: boolean;
  };

const PropertiesSelect = ({ rule, path, isRuleEdited, ...rest }: PropertiesSelectProps) => {
  const [options, setOptions] = useState<Property[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const form = Form.useFormInstance();
  const propertiesValue = Form.useWatch([...path, 'properties'], form);

  useEffect(() => {
    if (!isRuleEdited) return;
    const currentProperties = options?.filter((prop) => propertiesValue?.includes(prop?.property_name));
    form.setFieldValue([...path, 'properties'], currentProperties?.map((prop) => prop?.property_name));
  }, [options, propertiesValue, isRuleEdited]);

  useEffect(() => {

    // if (rest.disabled) return

    const controller = new AbortController();
    setIsLoading(true);
    const { filter_rule_id: _, ...payload } = formatRuleDataForApi(rule);
    if (!isClassLPAR(rule?.class)) {
      delete payload?.hw_type;
    }

    postApi<PropsResponseDTO, ItemGroupConfigurationRequest>(
      '/api/configuration/v1/props?type=tracker',
      payload,
      { signal: controller.signal }
    )
      .then(
        ({ data: { data } }) => setOptions(data),
        (reason) => Log.error('Failed to get properties!', reason)
      )
      .finally(() => setIsLoading(false));

    return () => controller.abort();
  }, [rule, rest.disabled]);

  return (
    <XmSelect
      {...rest}
      options={options}
      loading={isLoading}
      placeholder='Select Properties'
      mode='multiple'
      allowClear
      fieldNames={{ value: 'property_name' }}
      ignoreReadonly
    />
  );
};

export default FilterForm;
